// FullscreenButton — a small, faint control under the ads panel that puts the
// display into (or out of) browser fullscreen.
//
// Fullscreen is normally entered from the profile picker's click, but a TV
// that drops out of it (a remote press, a browser restarted straight into the
// display URL) had no way back without leaving the display. Browsers only
// grant fullscreen during a user gesture, so this has to be a real button —
// it is styled like the board's page arrows: barely there until hovered.
import React, { useEffect, useState } from "react";

const fullscreenElement = (): Element | null => {
  const d = document as any;
  return d.fullscreenElement || d.webkitFullscreenElement || d.msFullscreenElement || null;
};

const toggleFullscreen = async () => {
  const d = document as any;
  if (fullscreenElement()) {
    const exit = d.exitFullscreen || d.webkitExitFullscreen || d.msExitFullscreen;
    if (!exit) return;
    try {
      await exit.call(d);
    } catch {
      /* ignore */
    }
    return;
  }
  const el = document.documentElement as any;
  const req =
    el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
  if (!req) return;
  try {
    await req.call(el);
  } catch {
    /* not permitted — the display still fills the window */
  }
};

const FullscreenButton: React.FC = () => {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = () => setOn(!!fullscreenElement());
    sync();
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, []);

  return (
    <button
      aria-label={on ? "Exit fullscreen" : "Fullscreen"}
      title={on ? "Exit fullscreen" : "Fullscreen"}
      onClick={() => void toggleFullscreen()}
      className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-[0_1px_6px_rgba(16,24,40,0.12)] ring-1 ring-[#E9EDF3] opacity-30 transition-opacity duration-200 hover:opacity-100"
    >
      {/* rem-sized like everything else on the board (see useTVScale) */}
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="#1F2B4D"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-[0.875rem] w-[0.875rem]"
      >
        {on ? (
          <path d="M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" />
        ) : (
          <path d="M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6" />
        )}
      </svg>
    </button>
  );
};

export default FullscreenButton;
